import type { Building } from "building-types/building/buildingTypes";
import type { Casework } from "building-types/casework/caseworkTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { Prop } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RWTable } from "common/data/table";
import { getWindowCatalog } from "doc/elements/window/windowCatalog";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomLayoutElectricalMergeInto } from "../elements/electrical/electricalLegacyRoomLayoutUtils";
import type { RoomLayoutReturnValue } from "../elements/rooms/roomLayout";
import { exteriorRoomLayouts, interiorRoomLayouts } from "../elements/rooms/roomLayoutCatalog";
import { memoAsync } from "../utils/memo";

function select({
  doc: { rooms: docRooms },
  rooms,
  roomFloorAssemblies,
  settings,
  roomLevelIndices,
  levels,
  planarGraphs,
  planarGraphPerimeters,
  walls,
  roomLayoutElectrical,
  casework,
  props,
}: Building) {
  return {
    docRooms,
    rooms,
    roomLevelIndices,
    roomFloorAssemblies,
    levels,
    planarGraphs,
    planarGraphPerimeters,
    walls,
    propCatalog: settings.props,
    roomLayoutElectrical,
    windowCatalogSettings: settings.windowCatalog,
    caseworkFromBuilding: casework,
    propsFromBuilding: props,
  };
}

async function generate({
  rooms,
  docRooms,
  roomFloorAssemblies,
  planarGraphPerimeters,
  walls,
  propCatalog,
  roomLayoutElectrical,
  windowCatalogSettings,
  caseworkFromBuilding,
  propsFromBuilding,
}: ReturnType<typeof select>): Promise<Pick<Building, "props" | "casework" | "roomLayoutElectrical">> {
  const casework: RWTable<Casework> = { ...caseworkFromBuilding };
  const props: RWTable<Prop> = { ...propsFromBuilding };
  const updatedElectricTable = { ...roomLayoutElectrical };
  const windowCatalog = getWindowCatalog(windowCatalogSettings);

  function applyLayoutResult(room: Room, res: Partial<RoomLayoutReturnValue>) {
    if (res.props) {
      for (const prop of res.props) {
        props[prop.id] = prop;
      }
    }

    if (res.casework) {
      for (const cwk of res.casework) {
        casework[cwk.id] = cwk;
      }
    }

    if (res.electricLayouts) {
      const existing = updatedElectricTable[room.id];
      if (existing) {
        roomLayoutElectricalMergeInto(existing, res.electricLayouts);
      } else {
        updatedElectricTable[room.id] = res.electricLayouts;
      }
    }
  }

  for (const roomId in rooms) {
    const room = rooms[roomId];
    const baseRoomId = getSourceRoomId(room);
    const staticRoom = docRooms[baseRoomId];
    const interior = isInteriorRoomEnclosure[room.attributes.roomEnclosure];

    const floor = roomFloorAssemblies[baseRoomId];

    let res: Partial<RoomLayoutReturnValue> | undefined | null;

    if (staticRoom?.useBuiltinLayout) {
      if (interior) {
        const layout = interiorRoomLayouts[room.type];
        if (layout) {
          try {
            res = await layout.generate({
              room,
              floor,
              wallIndex: walls[room.level.id].wallIndex,
              planarGraphPerimeter: planarGraphPerimeters[room.level.id],
              palette: propCatalog,
              windowCatalog,
            });
          } catch (e) {
            // console.warn(`RoomLayoutError id="${roomLayoutId}", roomType="${room.type}"`, e);
          }
        }
      } else {
        const layout = exteriorRoomLayouts[room.type];
        if (layout) {
          try {
            res = await layout.generate({
              room,
              floor,
              wallIndex: walls[room.level.id].wallIndex,
              planarGraphPerimeter: planarGraphPerimeters[room.level.id],
              palette: propCatalog,
              windowCatalog,
            });
          } catch (e) {
            // console.warn(`RoomLayoutError - id="${roomLayoutId}", roomType="${room.type}"`, e);
          }
        }
      }
    }

    if (!res) {
      continue;
    }

    applyLayoutResult(room, res);
  }

  return {
    props,
    casework,
    roomLayoutElectrical: updatedElectricTable,
  };
}

export default memoAsync(select, generate);
